import { createFileRoute } from "@tanstack/react-router";
import { DataError } from "@/components/data-error";
import { useQuery } from "@tanstack/react-query";
import { queryDocs } from "@/integrations/firebase/data";
import { useAuth } from "@/hooks/use-auth";
import { Skeleton } from "@/components/ui/skeleton";
import { agentStatusLabel } from "@/lib/calls";
import { cn } from "@/lib/utils";
import { phoneKey, formatPhone } from "@/lib/phone";
import { Users } from "lucide-react";

export const Route = createFileRoute("/_app/agents")({
  head: () => ({ meta: [{ title: "Agents — EESWEB" }] }),
  component: AgentsPage,
});

type Agent = { id: string; name: string | null; current_status: string | null; extension: string | null; number: string | null; last_seen: string | null };

const ON_CALL = new Set(["busy", "on_call", "ringing"]);
const AVAILABLE = new Set(["idle", "available", "wrap"]);
// If Smartflo's hang-up never arrived, an agent would stay "on a call" forever.
// Past these ages without any event, the status is no longer believable.
const MAX_ON_CALL_MS = 4 * 3600_000;
const MAX_RINGING_MS = 15 * 60_000;
function effectiveStatus(a: Agent, now = Date.now()): string {
  const s = a.current_status ?? "offline";
  const age = a.last_seen ? now - new Date(a.last_seen).getTime() : Infinity;
  if (s === "ringing" && age > MAX_RINGING_MS) return "stale";
  if (ON_CALL.has(s) && age > MAX_ON_CALL_MS) return "stale";
  return s;
}
const GROUPS = [
  { key: "oncall", title: "On a call", dot: "bg-info", test: (s: string) => ON_CALL.has(s) },
  { key: "available", title: "Available", dot: "bg-success", test: (s: string) => AVAILABLE.has(s) },
  { key: "offline", title: "Offline or unknown", dot: "bg-muted-foreground/40", test: (s: string) => !ON_CALL.has(s) && !AVAILABLE.has(s) },
] as const;

function ago(iso: string | null): string {
  if (!iso) return "never";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  return new Date(iso).toLocaleDateString([], { day: "numeric", month: "short" });
}

function AgentsPage() {
  const { effectiveClientId } = useAuth();
  const { data: agents, isLoading, error: agentsError } = useQuery({
    queryKey: ["agents-list", effectiveClientId],
    queryFn: async () => {
      const rows = (await queryDocs<Omit<Agent, "id">>("agents", { clientId: effectiveClientId })) as Agent[];
      // Older data can hold the same person twice (once by Smartflo agent id, once by number).
      // Show one card per number, with the most recent activity and any known name.
      const byPerson = new Map<string, Agent>();
      for (const a of rows) {
        const key = phoneKey(a.number) ?? phoneKey(a.extension) ?? a.id;
        const prev = byPerson.get(key);
        if (!prev) { byPerson.set(key, a); continue; }
        const newer = String(a.last_seen ?? "") > String(prev.last_seen ?? "") ? a : prev;
        byPerson.set(key, { ...newer, name: newer.name ?? prev.name ?? a.name });
      }
      return [...byPerson.values()].sort((a, b) => String(a.name ?? "").localeCompare(String(b.name ?? "")));
    },
  });

  const list = agents ?? [];
  const onCall = list.filter((a) => ON_CALL.has(effectiveStatus(a))).length;
  const available = list.filter((a) => AVAILABLE.has(effectiveStatus(a))).length;

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <DataError error={agentsError} />
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">Agents</h1>
          <p className="text-sm text-muted-foreground">Who is free and who is on a call, as Smartflo reports it. Refreshes every 10 seconds.</p>
        </div>
        {list.length > 0 && (
          <p className="text-sm text-muted-foreground tabular">
            <span className="font-semibold text-foreground">{available}</span> available · <span className="font-semibold text-foreground">{onCall}</span> on a call · {list.length} total
          </p>
        )}
      </div>

      {isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-2xl" />)}</div>
      ) : list.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed px-6 py-16 text-center">
          <span className="grid size-10 place-items-center rounded-full bg-muted text-muted-foreground"><Users className="size-[18px]" /></span>
          <p className="text-sm font-medium">No agents yet</p>
          <p className="max-w-xs text-sm text-muted-foreground">Agents appear here automatically once Smartflo reports their first call.</p>
        </div>
      ) : (
        GROUPS.map((g) => {
          const rows = list.filter((a) => g.test(effectiveStatus(a)));
          if (rows.length === 0) return null;
          return (
            <section key={g.key} aria-label={g.title}>
              <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold">
                <span className={cn("size-2 rounded-full", g.dot)} /> {g.title} <span className="font-normal text-muted-foreground tabular">{rows.length}</span>
              </h2>
              <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {rows.map((a) => {
                  const s = effectiveStatus(a);
                  return (
                    <li key={a.id} className="flex items-center gap-3 rounded-2xl border bg-card p-4 shadow-soft">
                      <span className="relative grid size-10 shrink-0 place-items-center rounded-full bg-muted font-display text-sm font-semibold uppercase text-muted-foreground">
                        {(a.name ?? "?").trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("")}
                        <span className={cn("absolute -bottom-0.5 -right-0.5 size-3 rounded-full border-2 border-card", g.dot, g.key === "oncall" && "animate-pulse")} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium">{a.name ?? "Unnamed agent"}</div>
                        <div className="truncate text-xs text-muted-foreground tabular">
                          {formatPhone(a.number) ?? "No number"}{a.extension && a.extension !== a.number ? ` · ext ${a.extension}` : ""}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs font-medium">{s === "stale" ? "No recent activity" : agentStatusLabel[s] ?? s}</div>
                        <div className="text-xs text-muted-foreground">{ago(a.last_seen)}</div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })
      )}
    </div>
  );
}
